// apps/accounts/src/pages/materials/PurchaseOrders.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Search, Eye, Edit, Trash2, RefreshCw, ChevronLeft, ChevronRight,
  Truck, Building2, Calendar, X, User
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { useNavigate } from 'react-router-dom';

const statusConfig = {
  Draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  Sent: { label: 'Sent to Supplier', color: 'bg-blue-100 text-blue-700' },
  'Partial Received': { label: 'Partial Received', color: 'bg-amber-100 text-amber-700' },
  Received: { label: 'Received', color: 'bg-green-100 text-green-700' },
  Cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const PurchaseOrders = () => {
  const { role, user } = useAuth();
  const navigate = useNavigate();
  const canCreate = ['admin', 'accounts', 'site_supervisor'].includes(role);
  const canEdit = ['admin', 'accounts'].includes(role);
  const canDelete = ['admin'].includes(role);

  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // ✅ changed from vendors
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;

  // ─── Create Modal State ──────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    supplier: '',
    project: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery: '',
    note: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchSuppliers = async () => {
    try {
      const records = await pb.collection('suppliers').getFullList({ sort: 'companyName', requestKey: null });
      setSuppliers(records);
    } catch { console.warn('Could not fetch suppliers'); }
  };

  const fetchProjects = async () => {
    try {
      const records = await pb.collection('accounts_projects').getFullList({ sort: 'name', requestKey: null });
      setProjects(records);
    } catch { console.warn('Could not fetch projects'); }
  };

  const fetchOrders = async () => {
    try {
      const filters = [];
      if (search) filters.push(`(supplier.name ~ "${search}" || project.name ~ "${search}" || po_number ~ "${search}")`);
      if (filterStatus !== 'All') filters.push(`status = "${filterStatus}"`);
      const filter = filters.join(' && ');
      const result = await pb.collection('material_purchase_orders').getList(page, perPage, {
        filter: filter || undefined,
        sort: '-order_date',
        expand: 'supplier,project,created_by', // ✅ changed expand
        requestKey: null,
      });
      setOrders(result.items);
      setTotalPages(result.totalPages);
    } catch (err) {
      if (err.isAbort) return;
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchProjects();
    fetchOrders();
  }, [search, filterStatus, page]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleDelete = async (id, poNumber) => {
    if (!window.confirm(`Delete PO #${poNumber || id.slice(0, 8)}?`)) return;
    try {
      const items = await pb.collection('material_purchase_order_items').getFullList({
        filter: `purchase_order = "${id}"`,
        requestKey: null,
      });
      for (const item of items) await pb.collection('material_purchase_order_items').delete(item.id);
      await pb.collection('material_purchase_orders').delete(id);
      toast.success('Deleted');
      fetchOrders();
    } catch { toast.error('Delete failed'); }
  };

  // ─── Create Modal Handlers ──────────────────────────────────
  const openCreateModal = () => {
    setFormData({
      supplier: '',
      project: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_delivery: '',
      note: '',
    });
    setFormError('');
    setShowCreateModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = {
        supplier: formData.supplier, // ✅ changed from vendor
        project: formData.project || null,
        order_date: formData.order_date,
        expected_delivery: formData.expected_delivery || null,
        status: 'Draft',
        note: formData.note || null,
        created_by: user.id,
      };
      const record = await pb.collection('material_purchase_orders').create(data);
      toast.success('Purchase order created');
      setShowCreateModal(false);
      navigate(`/materials/purchase-orders/${record.id}`);
    } catch (err) {
      let msg = err.message || 'Failed';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────
  const statusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.Draft;
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" /></div>;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-amber-500" /> Purchase Orders
          </h1>
          <p className="text-sm text-slate-500">Manage material purchase orders from suppliers</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <Button onClick={openCreateModal} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Plus className="w-4 h-4 mr-1" /> New PO
            </Button>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border">
        <Input
          placeholder="Search supplier, project or PO..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="All">All Status</option>
          {Object.keys(statusConfig).map(key => (
            <option key={key} value={key}>{statusConfig[key].label}</option>
          ))}
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterStatus('All'); setPage(1); }}>
          Clear
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">PO #</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Supplier</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Project</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Order Date</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Total</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {orders.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No purchase orders</td></tr>
              ) : (
                orders.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-mono text-xs">{po.po_number || po.id.slice(0, 8)}</td>
                    <td className="px-4 py-3">{po.expand?.supplier?.companyName || po.expand?.supplier?.name || '—'}</td>
                    <td className="px-4 py-3">{po.expand?.project?.name || '—'}</td>
                    <td className="px-4 py-3">{formatDate(po.order_date)}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatCurrency(po.total_amount)}</td>
                    <td className="px-4 py-3">{statusBadge(po.status)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => navigate(`/materials/purchase-orders/${po.id}`)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {canEdit && po.status === 'Draft' && (
                          <button
                            onClick={() => navigate(`/materials/purchase-orders/edit/${po.id}`)}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && po.status === 'Draft' && (
                          <button
                            onClick={() => handleDelete(po.id, po.po_number)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 inline" /> Previous
          </button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
          >
            Next <ChevronRight className="w-4 h-4 inline" />
          </button>
        </div>
      )}

      {/* ─── Create Modal ────────────────────────────────────── */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">New Purchase Order</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Supplier *</label>
                <select
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                  required
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName || s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Project</label>
                <select
                  name="project"
                  value={formData.project}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                >
                  <option value="">Select Project (optional)</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Order Date *</label>
                  <Input
                    type="date"
                    name="order_date"
                    value={formData.order_date}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Expected Delivery</label>
                  <Input
                    type="date"
                    name="expected_delivery"
                    value={formData.expected_delivery}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Note (optional)</label>
                <Input
                  name="note"
                  value={formData.note}
                  onChange={handleFormChange}
                  placeholder="Additional instructions"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Creating...' : 'Create PO'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;